import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Plus, Pencil, Trash2, Loader2, LogOut, X, Eye, Video, Upload, Image, Instagram, Settings, Truck, FolderOpen, Copy, Star, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ImageLibraryPicker from "../components/ImageLibraryPicker";
import VariationImageInput from "../components/VariationImageInput";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { AdminLoadingState, AdminApiError } from "../components/admin/AdminErrorBoundary";
import AdminSidebar from "../components/admin/AdminSidebar";
import { AttachmentsPanel } from "../components/admin/AttachmentsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });
const sizes = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];
const emptyForm = { name: "", description: "", category: "", subcategory: "", size: "", color: "", price_4_day: "", price_8_day: "", price_per_day: "", bond_type: "fixed", bond_value: "", bond_refund_days: "7", deposit_amount: "", promotion_type: "none", promotion_value: "", blackout_dates: [], images: [], videos: [], conditions: "", available: true, rental_options: [], best_suited_for: [], rrp: "", procurement_cost: "", show_size_guide: true, variation_selection_mode: "single", display_message: "", audience: "women", is_visible: true };

// Default rental options for each category (auto-added when category is selected)
const defaultRentalOptions = {
  "Lehengas": [
    { id: "full_set", name: "Full Set (Blouse + Lehenga + Dupatta)", description: "Complete outfit including blouse, lehenga skirt, and dupatta", price_per_day: 0, price_4_day: 0, price_8_day: 0 },
    { id: "lehenga_dupatta", name: "Lehenga + Dupatta Only", description: "Lehenga skirt and dupatta without blouse - bring your own blouse", price_per_day: 0, price_4_day: 0, price_8_day: 0 }
  ],
  "Sarees": [
    { id: "full_set", name: "Full Set (Saree + Blouse)", description: "Complete outfit including saree and stitched blouse", price_per_day: 0, price_4_day: 0, price_8_day: 0 },
    { id: "saree_only", name: "Saree Only", description: "Saree fabric without blouse - bring your own blouse", price_per_day: 0, price_4_day: 0, price_8_day: 0 }
  ]
};

const generateOptionId = () => `opt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

export default function AdminOutfits() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [outfits, setOutfits] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOutfit, setEditingOutfit] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => { checkAuth(); fetchOutfits(); fetchCategories(); }, []);

  const checkAuth = async () => { const token = localStorage.getItem("adminToken"); if (!token) navigate("/admin/login"); };
  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });

  const fetchOutfits = async () => {
    try {
      // Admin Collection must show every product regardless of public
      // category/audience gating — including hidden drafts (Armaan) and
      // menswear while For Him is disabled. `include_hidden=true` bypasses
      // both the `is_visible` and category-gating filters on the backend.
      const response = await axios.get(`${API}/outfits?include_hidden=true`, getAuthHeaders());
      setOutfits(response.data);
    } catch { toast.error("Error loading outfits"); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoriesError(null);
      // Admin surfaces must list every category (including disabled ones)
      // so admins can still assign a product to For Him / Kurtas even when
      // that category is toggled off publicly.
      const res = await axios.get(`${API}/settings/categories/flat?include_disabled=true`);
      setDynamicCategories(res.data?.categories || []);
    } catch (err) {
      console.error("[AdminOutfits] Failed to load categories:", err);
      setCategoriesError("Failed to load categories. Please refresh or contact support.");
      setDynamicCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const parentCategories = (dynamicCategories || []).filter(c => !c.parent_id);
  const getSubcategories = (parentId) => (dynamicCategories || []).filter(c => c.parent_id === parentId);

  const handleOpenDialog = (outfit = null) => {
    if (outfit) { 
      setEditingOutfit(outfit); 
      setFormData({ 
        ...outfit, 
        videos: outfit.videos || [], 
        rental_options: outfit.rental_options || [],
        best_suited_for: outfit.best_suited_for || [],
        blackout_dates: outfit.blackout_dates || [],
        price_4_day: (outfit.price_4_day || "").toString(),
        price_8_day: (outfit.price_8_day || "").toString(),
        price_per_day: (outfit.price_per_day || "").toString(), 
        bond_type: outfit.bond_type || "fixed",
        bond_value: (outfit.bond_value || outfit.deposit_amount || "").toString(),
        bond_refund_days: (outfit.bond_refund_days || 7).toString(),
        deposit_amount: (outfit.deposit_amount || "").toString(),
        promotion_type: outfit.promotion_type || "none",
        promotion_value: (outfit.promotion_value || "").toString(),
        rrp: (outfit.rrp || "").toString(),
        procurement_cost: (outfit.procurement_cost || "").toString(),
        show_size_guide: outfit.show_size_guide !== false,
        variation_selection_mode: outfit.variation_selection_mode || "single",
        rental_info_message: outfit.rental_info_message || "",
        display_message: outfit.display_message || "",
        subcategory: outfit.subcategories?.[0] || outfit.category || "",
        audience: outfit.audience || "women",
        is_visible: outfit.is_visible !== false,
      }); 
      // Resolve parent category from subcategory
      const sub = (dynamicCategories || []).find(c => c.name === (outfit.subcategories?.[0] || outfit.category));
      if (sub?.parent_id) {
        setFormData(prev => ({ ...prev, category: sub.parent_id }));
      }
    } else { 
      setEditingOutfit(null); 
      setFormData(emptyForm); 
    }
    setImageUrl(""); setVideoUrl(""); setIsDialogOpen(true);
  };

  const handleDuplicate = (outfit) => {
    setEditingOutfit(null);
    setFormData({
      ...outfit,
      name: `${outfit.name} (Copy)`,
      videos: outfit.videos || [],
      rental_options: (outfit.rental_options || []).map(opt => ({ ...opt, id: generateOptionId() })),
      best_suited_for: outfit.best_suited_for || [],
      blackout_dates: outfit.blackout_dates || [],
      price_4_day: (outfit.price_4_day || "").toString(),
      price_8_day: (outfit.price_8_day || "").toString(),
      price_per_day: (outfit.price_per_day || "").toString(),
      bond_type: outfit.bond_type || "fixed",
      bond_value: (outfit.bond_value || outfit.deposit_amount || "").toString(),
      bond_refund_days: (outfit.bond_refund_days || 7).toString(),
      deposit_amount: (outfit.deposit_amount || "").toString(),
      promotion_type: outfit.promotion_type || "none",
      promotion_value: (outfit.promotion_value || "").toString(),
      rrp: (outfit.rrp || "").toString(),
      procurement_cost: (outfit.procurement_cost || "").toString(),
      show_size_guide: outfit.show_size_guide !== false,
      variation_selection_mode: outfit.variation_selection_mode || "single",
      rental_info_message: outfit.rental_info_message || "",
      display_message: outfit.display_message || "",
      subcategory: outfit.subcategories?.[0] || outfit.category || "",
      available: true,
    });
    // Resolve parent category for duplicate
    const sub = (dynamicCategories || []).find(c => c.name === (outfit.subcategories?.[0] || outfit.category));
    if (sub?.parent_id) {
      setFormData(prev => ({ ...prev, category: sub.parent_id }));
    }
    setImageUrl(""); setVideoUrl(""); setIsDialogOpen(true);
    toast.info("Duplicated! Edit the details and save as a new listing.");
  };

  const handleCloseDialog = () => { setIsDialogOpen(false); setEditingOutfit(null); setFormData(emptyForm); setImageUrl(""); setVideoUrl(""); };

  // Handle category change - auto-add rental options for Lehengas and Sarees
  const handleCategoryChange = (parentCatId) => {
    setFormData(prev => {
      const newData = { ...prev, category: parentCatId, subcategory: "" };
      return newData;
    });
  };

  const handleSubcategoryChange = (subName) => {
    setFormData(prev => {
      const newData = { ...prev, subcategory: subName };
      // Auto-add rental options for Lehengas and Sarees
      if (defaultRentalOptions[subName] && (!prev.rental_options || prev.rental_options.length === 0)) {
        newData.rental_options = defaultRentalOptions[subName].map(opt => ({
          ...opt,
          price_per_day: prev.price_per_day || 0
        }));
      }
      return newData;
    });
  };

  const handleRentalOptionChange = (optionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      rental_options: prev.rental_options.map(opt => 
        opt.id === optionId ? { ...opt, [field]: ['price_per_day','price_4_day','price_8_day','bond_value'].includes(field) ? (parseFloat(value) || 0) : value } : opt
      )
    }));
  };

  const handleAddRentalOption = () => {
    setFormData(prev => ({
      ...prev,
      rental_options: [...(prev.rental_options || []), { id: generateOptionId(), name: "", description: "", price_per_day: parseFloat(prev.price_per_day) || 0 }]
    }));
  };

  const handleRemoveRentalOption = (optionId) => {
    setFormData(prev => ({
      ...prev,
      rental_options: prev.rental_options.filter(opt => opt.id !== optionId)
    }));
  };

  const handleAddImage = () => { if (imageUrl && !formData.images.includes(imageUrl)) { setFormData(p => ({ ...p, images: [...p.images, imageUrl] })); setImageUrl(""); } };
  const handleRemoveImage = (url) => { setFormData(p => ({ ...p, images: p.images.filter(img => img !== url) })); };
  
  const handleAddVideo = () => { if (videoUrl && !formData.videos.includes(videoUrl)) { setFormData(p => ({ ...p, videos: [...p.videos, videoUrl] })); setVideoUrl(""); } };
  const handleRemoveVideo = (url) => { setFormData(p => ({ ...p, videos: p.videos.filter(v => v !== url) })); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(p => ({ ...p, images: [...p.images, imageUrl] }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid video (MP4, MOV, WebM, or MPEG)');
      return;
    }
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be less than 100MB');
      return;
    }
    
    try {
      setUploadingVideo(true);
      toast.info('Uploading video... This may take a moment.');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const videoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(p => ({ ...p, videos: [...p.videos, videoUrl] }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.subcategory || !formData.size || (!formData.price_4_day && !formData.price_per_day)) { toast.error("Please fill all required fields (Name, Category, Subcategory, Size, and at least 4-Day Price)"); return; }
    try {
      setSubmitting(true);
      const bondVal = parseFloat(formData.bond_value) || 0;
      const parentCat = (parentCategories || []).find(c => c.id === formData.category);
      const payload = { 
        ...formData, 
        category: formData.subcategory,
        categories: parentCat ? [parentCat.name] : [],
        subcategories: [formData.subcategory],
        price_per_day: parseFloat(formData.price_per_day) || 0, 
        price_4_day: parseFloat(formData.price_4_day) || 0,
        price_8_day: parseFloat(formData.price_8_day) || 0,
        deposit_amount: formData.bond_type === "fixed" ? bondVal : 0,
        bond_type: formData.bond_type,
        bond_value: bondVal,
        bond_refund_days: parseInt(formData.bond_refund_days) || 7,
        promotion_type: formData.promotion_type || "none",
        promotion_value: parseFloat(formData.promotion_value) || 0,
        blackout_dates: formData.blackout_dates || [],
        rrp: parseFloat(formData.rrp) || 0,
        procurement_cost: parseFloat(formData.procurement_cost) || 0,
        show_size_guide: formData.show_size_guide !== false,
        variation_selection_mode: formData.variation_selection_mode || "single",
        display_message: formData.display_message || "",
      };
      delete payload.subcategory;
      delete payload.rental_info_message;
      if (editingOutfit) { await axios.put(`${API}/outfits/${editingOutfit.id}`, payload, getAuthHeaders()); toast.success("Outfit updated"); } 
      else { await axios.post(`${API}/outfits`, payload, getAuthHeaders()); toast.success("Outfit created"); }
      handleCloseDialog(); fetchOutfits();
    } catch (error) { toast.error(error.response?.data?.detail || "Error saving outfit"); } 
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this outfit?")) return;
    try { await axios.delete(`${API}/outfits/${id}`, getAuthHeaders()); toast.success("Outfit deleted"); fetchOutfits(); } 
    catch { toast.error("Error deleting outfit"); }
  };

  const handleToggleFeatured = async (outfit) => {
    try {
      await axios.patch(`${API}/outfits/${outfit.id}/inline`, { featured: !outfit.featured }, getAuthHeaders());
      toast.success(outfit.featured ? "Removed from featured" : "Added to featured");
      fetchOutfits();
    } catch { toast.error("Failed to save"); }
  };

  const handleToggleQuickAdd = async (outfit) => {
    try {
      await axios.patch(`${API}/outfits/${outfit.id}/inline`, { quick_add_enabled: !outfit.quick_add_enabled }, getAuthHeaders());
      toast.success(outfit.quick_add_enabled ? "Quick Add disabled" : "Quick Add enabled");
      fetchOutfits();
    } catch { toast.error("Failed to save"); }
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />

      <main className="lg:ml-64 p-6 md:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div><h1 className="text-3xl text-[#676664]">Collection</h1><p className="text-[#676664]/40 text-sm font-light">Manage your pieces</p></div>
          <Button onClick={() => handleOpenDialog()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.15em] uppercase" data-testid="add-outfit-btn"><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Piece</Button>
        </div>

        {categoriesError && <AdminApiError message={categoriesError} onRetry={fetchCategories} />}

        {loading ? (
          <AdminLoadingState message="Loading collection..." />
        ) : (outfits || []).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(outfits || []).map((outfit) => (
              <div key={outfit.id} className="bg-white overflow-hidden border border-[#282828]/10">
                <div className="aspect-[4/3] bg-[#FAF8F0] relative overflow-hidden">
                  {outfit.images[0] ? <img src={outfit.images[0]} alt={outfit.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.parentElement.innerHTML='<div class="w-full h-full flex flex-col items-center justify-center text-[#9A7B4F]/30"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg><span class="text-xs mt-2">Re-upload needed</span></div>'; }} /> : <div className="w-full h-full flex items-center justify-center text-[#676664]/10"><Shirt className="w-16 h-16" strokeWidth={1} /></div>}
                  {!outfit.available && <div className="absolute top-3 left-3 bg-[#6B5B4F] text-white text-[10px] px-3 py-1 tracking-wider uppercase">Reserved</div>}
                </div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div><p className="text-[10px] text-[#9A7B4F] tracking-[0.15em] uppercase mb-1">{outfit.category}</p><h3 className="text-sm text-[#676664]">{outfit.name}</h3></div>
                    <div className="text-right">
                      {outfit.price_4_day > 0 && <p className="text-xs text-[#676664]">${outfit.price_4_day} <span className="text-[10px] text-[#676664]/40">/ 4d</span></p>}
                      {outfit.price_8_day > 0 && <p className="text-xs text-[#676664]">${outfit.price_8_day} <span className="text-[10px] text-[#676664]/40">/ 8d</span></p>}
                      {!outfit.price_4_day && !outfit.price_8_day && outfit.price_per_day > 0 && <p className="text-sm text-[#676664]">${outfit.price_per_day}/day</p>}
                    </div>
                  </div>
                  <p className="text-[11px] text-[#676664]/40 mb-4 font-light">Size: {outfit.size} • Bond: {outfit.bond_type === "percentage" ? `${outfit.bond_value || 0}%` : `$${outfit.bond_value || outfit.deposit_amount || 0}`}{outfit.promotion_type && outfit.promotion_type !== "none" && outfit.promotion_value > 0 ? ` • ${outfit.promotion_type === "percentage" ? `${outfit.promotion_value}% off` : `$${outfit.promotion_value} off`}` : ""}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleToggleFeatured(outfit)} className={`border-[#282828]/10 ${outfit.featured ? "text-amber-500 border-amber-300 bg-amber-50" : "text-[#676664]/30 hover:text-amber-400"}`} data-testid={`featured-outfit-${outfit.id}`} title={outfit.featured ? "Remove from featured" : "Add to featured"}><Star className={`w-3 h-3 ${outfit.featured ? "fill-amber-500" : ""}`} strokeWidth={1.5} /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleToggleQuickAdd(outfit)} className={`border-[#282828]/10 ${outfit.quick_add_enabled ? "text-green-600 border-green-300 bg-green-50" : "text-[#676664]/30 hover:text-green-500"}`} data-testid={`quick-add-outfit-${outfit.id}`} title={outfit.quick_add_enabled ? "Disable Quick Add" : "Enable Quick Add"}><ShoppingBag className="w-3 h-3" strokeWidth={1.5} /></Button>
                    <Link to={`/outfit/${outfit.id}`} className="flex-1"><Button variant="outline" size="sm" className="w-full border-[#282828]/10 text-[#676664] text-[10px]"><Eye className="w-3 h-3 mr-1" strokeWidth={1.5} />View</Button></Link>
                    <Button variant="outline" size="sm" onClick={() => handleDuplicate(outfit)} className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/10" data-testid={`duplicate-outfit-${outfit.id}`} title="Duplicate listing"><Copy className="w-3 h-3" strokeWidth={1.5} /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleOpenDialog(outfit)} className="border-[#282828]/10" data-testid={`edit-outfit-${outfit.id}`}><Pencil className="w-3 h-3" strokeWidth={1.5} /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(outfit.id)} className="border-[#282828]/10 text-red-500 hover:text-red-600" data-testid={`delete-outfit-${outfit.id}`}><Trash2 className="w-3 h-3" strokeWidth={1.5} /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-white border border-[#282828]/10">
            <Shirt className="w-16 h-16 text-[#676664]/10 mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-xl text-[#676664] mb-2">No pieces yet</h3>
            <p className="text-[#676664]/40 mb-6 font-light text-sm">Add your first piece to get started</p>
            <Button onClick={() => handleOpenDialog()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.15em] uppercase"><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add First Piece</Button>
          </div>
        )}
      </main>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!showImageLibrary) setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0"><DialogTitle className="text-2xl text-[#676664]">{editingOutfit ? "Edit Piece" : "Add New Piece"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-6 -webkit-overflow-scrolling-touch">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="text-[#676664]/60 text-[11px]">Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Royal Blue Silk Lehenga" className="mt-2 border-[#282828]/10" data-testid="outfit-name-input" required /></div>
              <div><Label className="text-[#676664]/60 text-[11px]">Category *</Label><Select value={formData.category} onValueChange={handleCategoryChange}><SelectTrigger className="mt-2 border-[#282828]/10" data-testid="outfit-category-select"><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent className="z-[200]">{(parentCategories || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-[#676664]/60 text-[11px]">Subcategory *</Label><Select value={formData.subcategory} onValueChange={handleSubcategoryChange} disabled={!formData.category}><SelectTrigger className="mt-2 border-[#282828]/10" data-testid="outfit-subcategory-select"><SelectValue placeholder={formData.category ? "Select subcategory" : "Select category first"} /></SelectTrigger><SelectContent className="z-[200]">{(getSubcategories(formData.category) || []).map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-[#676664]/60 text-[11px]">Size *</Label><Select value={formData.size} onValueChange={(v) => setFormData(p => ({ ...p, size: v }))}><SelectTrigger className="mt-2 border-[#282828]/10" data-testid="outfit-size-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent className="z-[200]">{sizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-[#676664]/60 text-[11px]">Color</Label><Input value={formData.color} onChange={(e) => setFormData(p => ({ ...p, color: e.target.value }))} placeholder="e.g., Royal Blue" className="mt-2 border-[#282828]/10" data-testid="outfit-color-input" /></div>
              <div><Label className="text-[#676664]/60 text-[11px]">4-Day Price ($) *</Label><Input type="number" step="0.01" min="0" value={formData.price_4_day} onChange={(e) => setFormData(p => ({ ...p, price_4_day: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="outfit-price-4day-input" placeholder="Flat price for 4-day rental" /></div>
              <div><Label className="text-[#676664]/60 text-[11px]">8-Day Price ($)</Label><Input type="number" step="0.01" min="0" value={formData.price_8_day} onChange={(e) => setFormData(p => ({ ...p, price_8_day: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="outfit-price-8day-input" placeholder="Flat price for 8-day rental" /></div>
              <div className="flex items-center gap-3"><Switch checked={formData.available} onCheckedChange={(c) => setFormData(p => ({ ...p, available: c }))} data-testid="outfit-available-switch" /><Label className="text-[#676664]/60 text-[11px]">Available</Label></div>
              <div className="flex items-center gap-3"><Switch checked={formData.is_visible !== false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_visible: c }))} data-testid="outfit-visible-switch" /><Label className="text-[#676664]/60 text-[11px]">Visible on website</Label></div>
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Audience</Label>
                <Select value={formData.audience || "women"} onValueChange={(v) => setFormData(p => ({ ...p, audience: v }))}>
                  <SelectTrigger className="mt-2 border-[#282828]/10" data-testid="outfit-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="women">Women</SelectItem>
                    <SelectItem value="men">Men</SelectItem>
                    <SelectItem value="unisex">Unisex</SelectItem>
                    <SelectItem value="kids">Kids</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3"><Switch checked={formData.show_size_guide} onCheckedChange={(c) => setFormData(p => ({ ...p, show_size_guide: c }))} data-testid="outfit-size-guide-toggle" /><Label className="text-[#676664]/60 text-[11px]">Show Size Guide</Label></div>
            </div>
            <div><Label className="text-[#676664]/60 text-[11px]">Description</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="mt-2 border-[#282828]/10 min-h-[80px]" data-testid="outfit-description-input" /></div>

            {/* Product Display Settings */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <Label className="text-[#676664] text-sm font-medium mb-3 block">Product Display Settings</Label>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Variation Selection Mode</Label>
                  <Select value={formData.variation_selection_mode} onValueChange={(v) => setFormData(p => ({ ...p, variation_selection_mode: v }))}>
                    <SelectTrigger className="mt-2 border-[#282828]/10" data-testid="variation-selection-mode"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="single">Single Selection (customer picks one option)</SelectItem>
                      <SelectItem value="multiple">Multiple Selection (customer can pick multiple, prices sum)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#676664]/50 mt-1">Single: customer selects one rental option. Multiple: customer can select several, and prices are added together.</p>
                </div>
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Display Message</Label>
                  <Textarea value={formData.display_message} onChange={(e) => setFormData(p => ({ ...p, display_message: e.target.value }))} className="mt-2 border-[#282828]/10 min-h-[60px]" data-testid="display-message-input" placeholder="Optional message shown under rental options. Leave blank to show nothing." />
                </div>
              </div>
            </div>
            
            {/* Security Bond */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <Label className="text-[#676664] text-sm font-medium mb-3 block">Security Bond*</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Bond Type</Label>
                  <Select value={formData.bond_type} onValueChange={(v) => setFormData(p => ({ ...p, bond_type: v }))}>
                    <SelectTrigger className="mt-2 border-[#282828]/10" data-testid="bond-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                      <SelectItem value="percentage">Percentage of Rental (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">{formData.bond_type === "percentage" ? "Bond %" : "Bond Amount ($)"}</Label>
                  <Input type="number" step="0.01" min="0" value={formData.bond_value} onChange={(e) => setFormData(p => ({ ...p, bond_value: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="bond-value-input" placeholder={formData.bond_type === "percentage" ? "e.g., 50" : "e.g., 200"} />
                </div>
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Refund Within (days after return)</Label>
                  <Input type="number" min="1" max="30" value={formData.bond_refund_days} onChange={(e) => setFormData(p => ({ ...p, bond_refund_days: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="bond-refund-days-input" />
                </div>
              </div>
            </div>

            {/* RRP + Procurement */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Recommended Retail Price (RRP)</Label>
                <Input type="number" step="0.01" min="0" value={formData.rrp} onChange={(e) => setFormData(p => ({ ...p, rrp: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="rrp-input" placeholder="e.g., 2500" />
              </div>
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Procurement cost (AUD, incl. GST)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={formData.procurement_cost}
                  onChange={(e) => setFormData(p => ({ ...p, procurement_cost: e.target.value }))}
                  className="mt-2 border-[#282828]/10"
                  data-testid="procurement-cost-input"
                  placeholder="e.g., 450"
                />
                <p className="text-[10px] text-[#676664]/60 mt-1 leading-snug">
                  What you paid for this outfit. Optional. Kept separate from operating costs.
                </p>
              </div>
            </div>

            {/* Section 18.5 — Procurement recovery snapshot (edit only) */}
            {editingOutfit ? (
              <ProcurementRecoveryPanel outfitId={editingOutfit.id} />
            ) : null}

            {/* Section 21 — Procurement Attachments (edit only) */}
            {editingOutfit ? (
              <div className="mt-2">
                <AttachmentsPanel parentType="outfit_procurement" parentId={editingOutfit.id} />
              </div>
            ) : null}

            {/* Promotion */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <Label className="text-[#676664] text-sm font-medium mb-3 block">Promotion</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Discount Type</Label>
                  <Select value={formData.promotion_type || "none"} onValueChange={(v) => setFormData(p => ({ ...p, promotion_type: v }))}>
                    <SelectTrigger className="mt-2 border-[#282828]/10" data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="none">No Promotion</SelectItem>
                      <SelectItem value="percentage">Percentage Off</SelectItem>
                      <SelectItem value="fixed">Fixed Discount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.promotion_type !== "none" && (
                  <div>
                    <Label className="text-[#676664]/60 text-[11px]">{formData.promotion_type === "percentage" ? "Discount %" : "Discount Amount ($)"}</Label>
                    <Input type="number" step="0.01" min="0" value={formData.promotion_value} onChange={(e) => setFormData(p => ({ ...p, promotion_value: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="promo-value-input" placeholder={formData.promotion_type === "percentage" ? "e.g., 10" : "e.g., 25"} />
                  </div>
                )}
              </div>
            </div>

            {/* Blackout Dates */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#676664] text-sm font-medium">Blackout Dates</Label>
                <Button type="button" onClick={() => { const d = prompt("Enter date (YYYY-MM-DD):"); if (d && /^\d{4}-\d{2}-\d{2}$/.test(d) && !formData.blackout_dates.includes(d)) setFormData(p => ({ ...p, blackout_dates: [...p.blackout_dates, d].sort() })); }} variant="outline" size="sm" className="border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/10 text-[10px]" data-testid="add-blackout-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Date
                </Button>
              </div>
              <p className="text-[10px] text-[#676664]/50 mb-3">Block specific dates when this item is unavailable (e.g., cleaning, events).</p>
              {formData.blackout_dates && formData.blackout_dates.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.blackout_dates.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1 bg-white border border-[#282828]/10 px-2 py-1 text-[11px] text-[#676664]">
                      {d}
                      <button type="button" onClick={() => setFormData(p => ({ ...p, blackout_dates: p.blackout_dates.filter(bd => bd !== d) }))} className="text-red-400 hover:text-red-600 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-[#676664]/40 italic text-center py-2">No blackout dates set.</p>
              )}
            </div>
            
            {/* Rental Options - Available for all categories */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#676664] text-sm font-medium">Rental Options</Label>
                <Button type="button" onClick={handleAddRentalOption} variant="outline" size="sm" className="border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/10 text-[10px]" data-testid="add-rental-option-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
              <p className="text-[10px] text-[#676664]/50 mb-4">
                Add rental options to let customers choose between variations (e.g., Full Set vs. individual pieces). Leave empty if no options needed.
              </p>
              {formData.rental_options && formData.rental_options.length > 0 ? (
                <div className="space-y-3">
                  {formData.rental_options.map((option, idx) => (
                    <div key={option.id} className="bg-white p-3 border border-[#282828]/5 space-y-2" data-testid={`rental-option-${idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 space-y-2">
                          <Input 
                            value={option.name} 
                            onChange={(e) => handleRentalOptionChange(option.id, 'name', e.target.value)}
                            placeholder="Option name (e.g., Full Set)"
                            className="border-[#282828]/10 text-sm"
                            data-testid={`rental-option-name-${idx}`}
                          />
                          <Input 
                            value={option.description} 
                            onChange={(e) => handleRentalOptionChange(option.id, 'description', e.target.value)}
                            placeholder="Description (e.g., Includes blouse, lehenga & dupatta)"
                            className="border-[#282828]/10 text-[11px]"
                            data-testid={`rental-option-desc-${idx}`}
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveRentalOption(option.id)} 
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                            data-testid={`remove-rental-option-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div>
                          <Label className="text-[#676664]/50 text-[10px]">4-Day Price ($)</Label>
                          <Input type="number" step="0.01" min="0" value={option.price_4_day || ""} onChange={(e) => handleRentalOptionChange(option.id, 'price_4_day', e.target.value)} className="border-[#282828]/10 text-sm mt-1" placeholder="0.00" data-testid={`rental-option-price4-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[#676664]/50 text-[10px]">8-Day Price ($)</Label>
                          <Input type="number" step="0.01" min="0" value={option.price_8_day || ""} onChange={(e) => handleRentalOptionChange(option.id, 'price_8_day', e.target.value)} className="border-[#282828]/10 text-sm mt-1" placeholder="0.00" data-testid={`rental-option-price8-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[#676664]/50 text-[10px]">Bond ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={option.bond_value ?? ""}
                            onChange={(e) => handleRentalOptionChange(option.id, 'bond_value', e.target.value)}
                            className="border-[#282828]/10 text-sm mt-1"
                            placeholder={`(uses ${formData.bond_value || 0})`}
                            data-testid={`rental-option-bond-${idx}`}
                            title="Leave empty to use the product-level bond above."
                          />
                        </div>
                      </div>
                      <div className="mt-2">
                        <Label className="text-[#676664]/50 text-[10px]">Variation Image (optional)</Label>
                        <VariationImageInput
                          value={option.image || ""}
                          onChange={(url) => handleRentalOptionChange(option.id, 'image', url)}
                          testIdPrefix={`rental-option-${idx}-`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-[#676664]/40 italic text-center py-3">No rental options added. Click "Add Option" to create one.</p>
              )}
              {(formData.category === "Lehengas" || formData.category === "Sarees") && (!formData.rental_options || formData.rental_options.length === 0) && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFormData(prev => ({ ...prev, rental_options: defaultRentalOptions[prev.category]?.map(opt => ({ ...opt, price_per_day: parseFloat(prev.price_per_day) || 0 })) || [] }))}
                  className="mt-2 w-full border-dashed border-[#9A7B4F]/40 text-[#9A7B4F] text-[10px]"
                  data-testid="load-default-options-btn"
                >
                  Load Default {formData.category} Options
                </Button>
              )}
            </div>
            
            <div><Label className="text-[#676664]/60 text-[11px]">Rental Terms</Label><Textarea value={formData.conditions} onChange={(e) => setFormData(p => ({ ...p, conditions: e.target.value }))} className="mt-2 border-[#282828]/10" data-testid="outfit-conditions-input" /></div>
            
            {/* Best Suited For - SEO bullet points */}
            <div className="bg-[#FAF8F0] p-4 border border-[#282828]/10">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#676664] text-sm font-medium">Best Suited For</Label>
                <Button 
                  type="button" 
                  onClick={() => setFormData(p => ({ ...p, best_suited_for: [...(p.best_suited_for || []), ""] }))} 
                  variant="outline" size="sm" 
                  className="border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/10 text-[10px]"
                  data-testid="add-suited-for-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Bullet
                </Button>
              </div>
              <p className="text-[10px] text-[#676664]/50 mb-3">Add SEO-friendly bullet points (e.g., "Indian weddings", "Mehendi ceremonies"). Leave empty to hide this section.</p>
              {formData.best_suited_for && formData.best_suited_for.length > 0 ? (
                <div className="space-y-2">
                  {formData.best_suited_for.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-[#9A7B4F] text-xs">•</span>
                      <Input 
                        value={item} 
                        onChange={(e) => setFormData(p => ({ ...p, best_suited_for: p.best_suited_for.map((v, i) => i === idx ? e.target.value : v) }))}
                        placeholder="e.g., Indian weddings, Mehendi ceremonies"
                        className="border-[#282828]/10 text-sm flex-1"
                        data-testid={`suited-for-${idx}`}
                      />
                      <Button 
                        type="button" variant="ghost" size="sm"
                        onClick={() => setFormData(p => ({ ...p, best_suited_for: p.best_suited_for.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      ><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-[#676664]/40 italic text-center py-2">No bullet points added. Click "Add Bullet" to create one.</p>
              )}
            </div>
            <div>
              <Label className="text-[#676664]/60 text-[11px]">Images</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Paste image URL" className="border-[#282828]/10 flex-1 min-w-[120px]" data-testid="outfit-image-url-input" />
                <Button type="button" onClick={handleAddImage} variant="outline" className="border-[#282828]/10" data-testid="add-image-btn"><Plus className="w-4 h-4" /></Button>
                <input type="file" ref={imageInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" />
                <Button type="button" onClick={() => imageInputRef.current?.click()} variant="outline" className="border-[#282828]/10" disabled={uploadingImage} data-testid="upload-image-btn">
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
                <Button type="button" onClick={() => setShowImageLibrary(true)} variant="outline" className="border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/10" data-testid="browse-library-btn">
                  <FolderOpen className="w-4 h-4 mr-1" /><span className="text-xs">Library</span>
                </Button>
              </div>
              {formData.images?.length > 0 && <div className="flex flex-wrap gap-3 mt-4">{(formData.images || []).map((img, i) => <div key={i} className="relative group"><img src={img} alt="" className="w-20 h-20 object-cover" /><button type="button" onClick={() => handleRemoveImage(img)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 text-xs" data-testid={`remove-image-${i}`}>×</button></div>)}</div>}
            </div>
            <div>
              <Label className="text-[#676664]/60 text-[11px]">Videos</Label>
              <div className="mt-2 flex gap-2">
                <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Paste video URL (MP4, YouTube, etc.)" className="border-[#282828]/10 flex-1" data-testid="outfit-video-url-input" />
                <Button type="button" onClick={handleAddVideo} variant="outline" className="border-[#282828]/10" data-testid="add-video-btn"><Plus className="w-4 h-4" /></Button>
                <input type="file" ref={videoInputRef} onChange={handleVideoUpload} accept="video/mp4,video/quicktime,video/webm,video/mpeg" className="hidden" />
                <Button type="button" onClick={() => videoInputRef.current?.click()} variant="outline" className="border-[#9A7B4F] bg-[#9A7B4F]/10 hover:bg-[#9A7B4F]/20" disabled={uploadingVideo} data-testid="upload-video-btn">
                  {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Video className="w-4 h-4 mr-1" /><span className="text-xs">Upload</span></>}
                </Button>
              </div>
              {formData.videos && formData.videos.length > 0 && <div className="flex flex-wrap gap-3 mt-4">{formData.videos.map((vid, i) => <div key={i} className="relative group bg-[#6B5B4F]/5 p-3 flex items-center gap-2"><Video className="w-4 h-4 text-[#9A7B4F]" /><span className="text-xs text-[#676664]/60 max-w-[150px] truncate">{vid.split('/').pop()}</span><button type="button" onClick={() => handleRemoveVideo(vid)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 text-xs">×</button></div>)}</div>}
            </div>
            </div>
            <div className="flex-shrink-0 border-t border-[#282828]/10 bg-white px-6 py-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-[#282828]/10">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="save-outfit-btn">{submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : editingOutfit ? "Update" : "Add Piece"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <ImageLibraryPicker 
        open={showImageLibrary} 
        onClose={() => setShowImageLibrary(false)} 
        multiple={true}
        onSelect={(urls) => {
          if (Array.isArray(urls)) {
            setFormData(p => ({ ...p, images: [...p.images, ...urls.filter(u => !p.images.includes(u))] }));
          }
        }}
      />
    </div>
  );
}

// ─── Section 18.5 — Procurement Recovery Panel ──────────────────────────
function ProcurementRecoveryPanel({ outfitId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    axios
      .get(`${API}/admin/profitability/procurement-recovery?outfit_id=${outfitId}`, authHeaders())
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "load_failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [outfitId]);

  if (loading) {
    return (
      <div className="border border-[#282828]/10 bg-[#FAF8F0] p-4 flex items-center gap-2 text-xs text-[#676664]" data-testid="procurement-recovery-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading procurement recovery…
      </div>
    );
  }
  if (err) {
    return (
      <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="procurement-recovery-error">
        Could not load procurement recovery: {err}
      </div>
    );
  }
  if (!data) return null;

  const entered = !!data.procurement_cost_entered;
  const recovered = !!data.cost_recovered;

  return (
    <div className="border border-[#282828]/10 bg-[#FAF8F0] p-4" data-testid="procurement-recovery-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] tracking-widest uppercase text-[#9A7B4F]">Procurement recovery</p>
          <p className="text-xs text-[#676664]/70 mt-0.5">Lifetime, this outfit only. Kept separate from operating costs.</p>
        </div>
        {entered && (
          <span
            className={`text-[10px] px-2 py-1 uppercase tracking-widest border ${
              recovered ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
            data-testid="recovery-badge"
          >
            {recovered ? "Cost recovered" : "Not yet recovered"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <Stat label="Procurement cost" value={entered ? `$${data.procurement_cost.toFixed(2)}` : "Not entered"} testid="stat-procurement-cost" />
        <Stat label="Lifetime revenue" value={`$${(data.lifetime_rental_revenue || 0).toFixed(2)}`} testid="stat-lifetime-revenue" />
        <Stat label="Completed paid rentals" value={String(data.completed_paid_rentals || 0)} testid="stat-completed-rentals" />
        <Stat label="Revenue − cost" value={entered ? `$${data.revenue_less_procurement.toFixed(2)}` : "—"} testid="stat-revenue-less-cost" />
        <Stat label="Recovery %" value={entered ? `${data.recovery_pct.toFixed(1)}%` : "Not entered"} testid="stat-recovery-pct" />
        <Stat label="Cost recovered" value={entered ? (recovered ? "Yes" : "No") : "—"} testid="stat-cost-recovered" />
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest uppercase text-[#676664]/60">{label}</p>
      <p className="text-[#282828] font-medium mt-0.5" data-testid={testid}>{value}</p>
    </div>
  );
}

